import React from "react";
import {
  Popover,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
} from "@mui/material";
import { DescriptionOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { tokens } from "../../theme";
import { useAppDispatch } from "../../redux/slice/redux-hooks";

dayjs.extend(relativeTime);

interface RowData {
  notificationId: number;
  orderNumber: string;
  description: string;
  dateTime: string;
}

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAcknowledge: (notification: RowData) => void;
  rows: RowData[];
}

const StatusInfoPopup: React.FC<Props> = ({ anchorEl, onClose,onAcknowledge, rows }) => {
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formatTimeAgo = (date: string) => {
    const diff = dayjs().diff(dayjs(date), "hour");
    return diff > 48
      ? dayjs(date).format("MMM D, YYYY")
      : dayjs(date).fromNow();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          bgcolor: colors.grey[900],
          borderRadius: "12px",
          boxShadow: 4,
          width: 380,
        },
      }}
    >
      <Typography variant="h5" fontWeight="bold" p={2}>
        Notifications ({rows.length})
      </Typography>
      <Divider />

      {rows.map((item, index) => (
        <Box key={index} mb={index < rows.length - 1 ? 2 : 0}>
          <Box sx={{ padding: "5px 10px" }}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box
                sx={{
                  width: 36,
                  minWidth: 36, 
                  height: 36,
                  borderRadius: "50%",
                  border: `2px solid ${colors.grey[600]}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[200],
                  background: colors.grey[800],
                  flexShrink: 0, 
                }}
              >
                <DescriptionOutlined fontSize="small" />
              </Box>

              <Typography
                fontWeight="bold"
                sx={{
                  whiteSpace: "normal", 
                  wordBreak: "break-word", 
                  flex: 1,
                }}
              >
                {item.description}
              </Typography>
            </Box>


            <Typography variant="body2" color="gray" ml={5} mt={0.5}>
              {formatTimeAgo(item.dateTime)}
            </Typography>

            <Box ml={5} mt={1}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  fontWeight: "bold",
                  backgroundColor: colors.grey[300],
                  color: colors.grey[900],
                  textTransform: "none",
                  px: 2,
                  mb: 2,
                  "&:hover": {
                    backgroundColor: colors.grey[300],
                  },
                }}
                onClick={()=>{
                  onAcknowledge(item);
                  onClose();
                }                
                }
              >
                Acknowledge
              </Button>
            </Box>
          </Box>

          {index < rows.length - 1 && (
            <Divider
              sx={{
                borderStyle: "dashed",
                borderColor: colors.grey[600],
              }}
            />
          )}
        </Box>
      ))}
    </Popover>
  );
};

export default StatusInfoPopup;
